import { useEffect, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { parseTimeInput } from '../../utils/timeInput';

/**
 * D-292 - a time field you can TYPE in: "0930", "930", "9:30", "09:30" all become 09:30.
 * Same value contract as `<input type="time">` (`HH:mm`, '' when empty / not a time), so the
 * callers' state and what they send do not change. An invalid text is marked (aria-invalid + red).
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'defaultValue'> & {
  value: string;
  onChange: (value: string) => void;
};

export default function TimeTextInput({ value, onChange, className, onBlur, ...rest }: Props) {
  const [draft, setDraft] = useState<string | null>(null);
  // an outside change (not the echo of what was typed) replaces the draft
  useEffect(() => { setDraft((d) => (d !== null && (parseTimeInput(d) ?? '') === value ? d : null)); }, [value]);
  const text = draft ?? value;
  const invalid = draft !== null && draft.trim() !== '' && parseTimeInput(draft) === null;
  return (
    <input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      dir="ltr"
      placeholder={rest.placeholder ?? 'HH:mm'}
      maxLength={5}
      value={text}
      aria-invalid={invalid || undefined}
      className={`${className ?? 'input'}${invalid ? ' input-error' : ''}`}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        onChange(parseTimeInput(raw) ?? ''); // like the native field: not a time -> ''
      }}
      onBlur={(e) => {
        const p = parseTimeInput(draft ?? value);
        if (p) setDraft(null); // show the normalised HH:mm
        onBlur?.(e);
      }}
    />
  );
}
